// Split out of journey.ts (ADR-002 phase C): dashboard/view chrome
// rendering, element/observation mutations and the onboarding first-run flow.

import { $ } from '../shared/dom';
import { showToast } from '../shared/ui';
import { apiFetch } from '../api/client';
import { onboardingOverlay, type OnboardingAnswers } from '../shared/onboarding-gateway';
import { trajState, trajActions, isJourneyReadOnly, escapeHtml } from './journey-state';
import { openObservationDrawer, closeObservationDrawer } from './journey-drawer';
import type { GraphNode } from './D3JourneyGraph';
import type {
  Journey,
  JourneyCategory,
  JourneyElement,
  JourneyObservation,
  JourneyRole,
  JourneyState,
} from './journey.types';

function formatJourneyRole(role: JourneyRole | string): string {
  switch (role) {
    case 'owner': return 'Владелец';
    case 'editor': return 'Редактор';
    case 'reader': return 'Читатель';
    default: return String(role);
  }
}

export function updateJourneyRoleBadge(role: JourneyRole | string | null) {
  const roleBadge = $('journey-role-badge');
  if (!roleBadge) return;
  if (!role) {
    roleBadge.textContent = '—';
    roleBadge.className = 'journey-role-badge';
    return;
  }
  roleBadge.textContent = formatJourneyRole(role);
  roleBadge.className = `journey-role-badge role-${role}`;
}

export function setActiveViewTab(tab: 'columns' | 'mycelium') {
  const tabColumns = $('tab-columns-view');
  const tabMycelium = $('tab-mycelium-view');
  tabColumns?.classList.toggle('active', tab === 'columns');
  tabMycelium?.classList.toggle('active', tab === 'mycelium');
  tabColumns?.setAttribute('aria-selected', tab === 'columns' ? 'true' : 'false');
  tabMycelium?.setAttribute('aria-selected', tab === 'mycelium' ? 'true' : 'false');
}

export function syncViewModeFromJourneyId(id: string) {
  const t = trajState.allJourneys.find((x) => x.id === id);
  if (t) trajState.currentViewMode = t.role === 'owner' ? 'my' : 'shared';
}

export function updateJourneySelectTooltip(journey: Journey | null) {
  const select = $<HTMLSelectElement>('journey-select');
  const tooltip = $('journey-select-tooltip');
  const desc = journey?.description?.trim() || '';

  if (select) {
    select.title = desc;
    select.dataset.journeyDesc = desc;
  }
  if (tooltip) {
    tooltip.textContent = desc;
    tooltip.classList.add('hidden');
  }
}

export function bindJourneySelectTooltip() {
  const wrap = $('journey-select-wrap');
  const tooltip = $('journey-select-tooltip');
  const select = $<HTMLSelectElement>('journey-select');
  if (!wrap || !tooltip || !select) return;

  const show = () => {
    const desc = select.dataset.journeyDesc;
    if (!desc) return;
    tooltip.textContent = desc;
    tooltip.classList.remove('hidden');
  };
  const hide = () => {
    tooltip.classList.add('hidden');
  };

  wrap.addEventListener('mouseenter', show);
  wrap.addEventListener('mouseleave', hide);
  select.addEventListener('focus', show);
  select.addEventListener('blur', hide);
}

export function updateOnboardingGuideButton() {
  const btn = $<HTMLButtonElement>('btn-onboarding-guide');
  if (!btn) return;
  btn.hidden = !trajState.onboardingEnabled || onboardingOverlay.isActive();
}

export function openOnboardingGuide() {
  if (!trajState.onboardingEnabled || onboardingOverlay.isActive()) return;
  renderOnboardingFirstRun();
}

export function setJourneyView(mode: 'columns' | 'mycelium') {
  trajState.currentViewTab = mode;
  setActiveViewTab(mode);

  const body = $('journey-view-body');
  if (body) body.dataset.activeView = mode;

  const dashboard = $('journey-dashboard-grid');
  const mycelium = $('journey-mycelium-container');
  dashboard?.classList.toggle('hidden', mode !== 'columns');
  mycelium?.classList.toggle('hidden', mode !== 'mycelium');
}

export function resetViewToColumns() {
  setJourneyView('columns');

  if (trajState.d3Graph) {
    trajState.d3Graph.destroy();
    trajState.d3Graph = null;
  }
}

export function setMyceliumTabEnabled(enabled: boolean) {
  const tab = $<HTMLButtonElement>('tab-mycelium-view');
  if (!tab) return;
  tab.disabled = !enabled;
  tab.style.opacity = enabled ? '1' : '0.45';
  tab.style.cursor = enabled ? 'pointer' : 'not-allowed';
  if (!enabled && trajState.currentViewTab === 'mycelium') {
    $('tab-columns-view')?.click();
  }
}

export function renderOnboardingFirstRun() {
  closeObservationDrawer();
  resetViewToColumns();
  onboardingOverlay.hide();

  updateJourneySelectTooltip(null);

  const select = $<HTMLSelectElement>('journey-select');
  if (select) select.disabled = true;

  updateJourneyRoleBadge(null);

  const deleteBtn = $<HTMLButtonElement>('btn-delete-journey');
  if (deleteBtn) deleteBtn.disabled = true;
  const editBtn = $<HTMLButtonElement>('btn-edit-journey');
  if (editBtn) editBtn.disabled = true;

  const dashboard = $('journey-dashboard-grid');
  if (dashboard) dashboard.innerHTML = '';

  onboardingOverlay.render({
    onExitToCabinet: () => {
      void trajActions.reloadJourneys();
    },
    onDraftComplete: async (answers: OnboardingAnswers) => {
      await saveOnboardingDraft(answers);
    },
  });
  updateOnboardingGuideButton();
}

async function saveOnboardingDraft(answers: OnboardingAnswers) {
  const hasContent = Object.values(answers).some((v) => v.trim().length > 0);
  if (!hasContent) {
    showToast('Добавьте хотя бы одну запись перед сохранением', 'warning');
    return;
  }

  try {
    const result = await apiFetch<{
      journey: Journey;
      observation: JourneyObservation;
      cycles_created: number;
    }>('/api/journey/onboarding/draft', {
      method: 'POST',
      body: JSON.stringify({ ...answers, session_id: onboardingOverlay.getSessionId() }),
    });

    onboardingOverlay.hide();
    onboardingOverlay.markSeen();
    showToast(`Черновик сохранён (${result.cycles_created} шагов)`);
    await trajActions.reloadJourneys();
    const select = $<HTMLSelectElement>('journey-select');
    if (select && result.journey?.id) {
      select.value = result.journey.id;
      localStorage.setItem('avgexpert_last_journey_id', result.journey.id);
      await trajActions.reloadDetails(result.journey.id);
    }
  } catch (err: any) {
    console.error('Failed to save onboarding draft', err);
    showToast(err.message || 'Ошибка сохранения черновика', 'error');
  }
}

export function renderEmptyState() {
  if (onboardingOverlay.isActive()) onboardingOverlay.hide();
  closeObservationDrawer();
  resetViewToColumns();

  updateJourneySelectTooltip(null);

  const select = $<HTMLSelectElement>('journey-select');
  if (select) select.disabled = true;

  updateJourneyRoleBadge(null);

  const container = $('journey-dashboard-grid');
  if (container) {
    const guideHint = trajState.onboardingEnabled
      ? `<p style="margin-top: 12px;">Или нажмите <strong>Проводник</strong> вверху, чтобы пройти знакомство с методом.</p>`
      : '';
    container.innerHTML = `
      <div style="grid-column: span 3; text-align: center; padding: 40px; color: var(--muted);">
        <p>У вас пока нет карты наблюдений. Создайте историю кнопкой «+» выше.</p>
        ${guideHint}
      </div>
    `;
  }

  const deleteBtn = $<HTMLButtonElement>('btn-delete-journey');
  if (deleteBtn) deleteBtn.disabled = true;
  const editBtn = $<HTMLButtonElement>('btn-edit-journey');
  if (editBtn) editBtn.disabled = true;
}

export function renderDashboard(journey: Journey) {
  const container = $('journey-dashboard-grid');
  if (!container) return;

  const readOnly = isJourneyReadOnly(journey);
  const role = journey.role || 'reader';
  const canEdit = (role === 'owner' || role === 'editor') && !readOnly;

  // Enable/disable header actions
  const deleteBtn = $<HTMLButtonElement>('btn-delete-journey');
  if (deleteBtn) deleteBtn.disabled = (role !== 'owner' || readOnly);
  
  const editBtn = $<HTMLButtonElement>('btn-edit-journey');
  if (editBtn) editBtn.disabled = (role !== 'owner' || readOnly);

  container.innerHTML = `
    <!-- Сфера Work -->
    <div class="journey-sphere-column" data-sphere="work">
      <div class="journey-sphere-header">
        <span class="journey-sphere-title">💼 Работа</span>
        ${canEdit ? `<button class="btn-text add-element-btn" data-sphere="work">+ Добавить</button>` : ''}
      </div>
      <div class="journey-elements-list" id="sphere-work-list"></div>
    </div>

    <!-- Сфера Relation -->
    <div class="journey-sphere-column" data-sphere="relation">
      <div class="journey-sphere-header">
        <span class="journey-sphere-title">❤️ Отношения</span>
        ${canEdit ? `<button class="btn-text add-element-btn" data-sphere="relation">+ Добавить</button>` : ''}
      </div>
      <div class="journey-elements-list" id="sphere-relation-list"></div>
    </div>

    <!-- Сфера Project -->
    <div class="journey-sphere-column" data-sphere="project">
      <div class="journey-sphere-header">
        <span class="journey-sphere-title">🚀 Проекты</span>
        ${canEdit ? `<button class="btn-text add-element-btn" data-sphere="project">+ Добавить</button>` : ''}
      </div>
      <div class="journey-elements-list" id="sphere-project-list"></div>
    </div>
  `;

  // Bind add element buttons
  container.querySelectorAll('.add-element-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const sphere = (e.target as HTMLElement).dataset.sphere as JourneyCategory;
      if (sphere) promptAddElement(sphere);
    });
  });

  const workList = $('sphere-work-list')!;
  const relationList = $('sphere-relation-list')!;
  const projectList = $('sphere-project-list')!;

  const elements = journey.elements || [];
  elements.forEach(el => {
    const card = document.createElement('div');
    card.className = 'journey-element-card';
    card.dataset.id = el.id;

    const observations = el.observations || [];
    const statusOptions = ['alive', 'dead', 'ambiguous', 'red_zone', 'unknown'];
    const statusLabels: Record<string, string> = {
      alive: 'Живой',
      dead: 'Мертвый',
      ambiguous: 'Двоякий',
      red_zone: 'Красная зона',
      unknown: 'Неизвестно'
    };

    let statusSelectHtml: string;
    if (canEdit) {
      statusSelectHtml = `
        <select class="journey-status-select status-${el.state}" data-element-id="${el.id}">
          ${statusOptions.map(opt => `<option value="${opt}" ${el.state === opt ? 'selected' : ''}>${statusLabels[opt]}</option>`).join('')}
        </select>
      `;
    } else {
      statusSelectHtml = `
        <span class="journey-status-select status-${el.state}" style="cursor: default;">
          ${statusLabels[el.state] || el.state}
        </span>
      `;
    }

    card.innerHTML = `
      <div class="journey-element-header">
        <div style="flex: 1;">
          <div class="journey-element-title">${escapeHtml(el.title)}</div>
          ${el.description ? `<div class="journey-element-desc">${escapeHtml(el.description)}</div>` : ''}
        </div>
        <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 4px;">
          ${statusSelectHtml}
          ${canEdit ? `
            <div style="display: flex; gap: 4px; margin-top: 4px;">
              <button class="btn-action-icon edit-btn edit-element-btn" title="Редактировать">✏️</button>
              <button class="btn-action-icon delete-element-btn" title="Удалить">🗑️</button>
            </div>
          ` : ''}
        </div>
      </div>
      
      <div class="journey-observations-title">Наблюдения</div>
      <div class="journey-observations-list">
        ${observations.map(obs => `
          <div class="journey-observation-item" data-id="${obs.id}">
            <span class="journey-observation-item-title" title="${escapeHtml(obs.title)}">${escapeHtml(obs.title)}</span>
            <span class="journey-observation-item-arrow">→</span>
          </div>
        `).join('')}
        ${observations.length === 0 ? `<div style="font-size: 0.75rem; color: var(--muted); font-style: italic;">Наблюдений нет</div>` : ''}
      </div>
      ${canEdit ? `<button class="btn-text add-observation-btn" style="margin-top: 4px; align-self: flex-start;">+ Добавить наблюдение</button>` : ''}
    `;

    // Bind event listeners for card
    card.querySelectorAll('.journey-observation-item').forEach(item => {
      item.addEventListener('click', (e) => {
        const obsId = (e.currentTarget as HTMLElement).dataset.id;
        if (obsId) openObservationDrawer(obsId);
      });
    });

    if (canEdit) {
      const select = card.querySelector<HTMLSelectElement>('.journey-status-select');
      select?.addEventListener('change', async (e) => {
        const newStatus = (e.target as HTMLSelectElement).value as JourneyState;
        await updateElementState(el.id, newStatus);
      });

      card.querySelector('.edit-element-btn')?.addEventListener('click', () => promptEditElement(el));
      card.querySelector('.delete-element-btn')?.addEventListener('click', () => deleteElement(el.id));
      card.querySelector('.add-observation-btn')?.addEventListener('click', () => promptAddObservation(el.id));
    }

    if (el.category === 'work') workList.appendChild(card);
    else if (el.category === 'relation') relationList.appendChild(card);
    else if (el.category === 'project') projectList.appendChild(card);
  });
}

async function promptAddElement(category: JourneyCategory) {
  if (!trajState.currentJourney) return;
  const title = prompt('Введите название элемента сферы:');
  if (!title) return;
  const description = prompt('Введите описание (необязательно):') || '';
  
  try {
    await apiFetch(`/api/journeys/${trajState.currentJourney.id}/elements`, {
      method: 'POST',
      body: JSON.stringify({ category, title, description })
    });
    showToast('Элемент успешно создан');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка создания элемента', 'error');
  }
}

async function promptEditElement(el: JourneyElement) {
  if (!trajState.currentJourney) return;
  const title = prompt('Редактировать название элемента:', el.title);
  if (!title) return;
  const description = prompt('Редактировать описание:', el.description || '') || '';

  try {
    await apiFetch(`/api/journey-elements/${el.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title, description })
    });
    showToast('Элемент успешно изменен');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка изменения элемента', 'error');
  }
}

async function updateElementState(elementId: string, state: JourneyState) {
  if (!trajState.currentJourney) return;
  try {
    await apiFetch(`/api/journey-elements/${elementId}`, {
      method: 'PATCH',
      body: JSON.stringify({ state })
    });
    showToast('Статус элемента обновлен');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка обновления статуса', 'error');
  }
}

async function deleteElement(elementId: string) {
  if (!trajState.currentJourney) return;
  if (!confirm('Вы уверены, что хотите удалить этот элемент? Все связанные наблюдения и шаги будут удалены!')) return;
  try {
    await apiFetch(`/api/journey-elements/${elementId}`, {
      method: 'DELETE'
    });
    showToast('Элемент удален');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка удаления элемента', 'error');
  }
}

async function promptAddObservation(elementId: string) {
  if (!trajState.currentJourney) return;
  const title = prompt('Введите название наблюдения:');
  if (!title) return;
  const description = prompt('Введите описание наблюдения (необязательно):') || '';

  try {
    await apiFetch(`/api/journey-elements/${elementId}/observations`, {
      method: 'POST',
      body: JSON.stringify({ title, description })
    });
    showToast('Наблюдение создано');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка создания наблюдения', 'error');
  }
}

export function handleGraphNodeClick(node: GraphNode) {
  if (!trajState.currentJourney) return;

  switch (node.type) {
    case 'observation':
      openObservationDrawer(node.observationId);
      break;
    case 'element': {
      const el = trajState.currentJourney.elements?.find((e) => e.id === node.elementId);
      if (el) promptEditElement(el);
      else showToast('Элемент не найден', 'error');
      break;
    }
    case 'journey':
      $('btn-edit-journey')?.click();
      break;
    case 'mechanism': {
      const obsId = trajState.d3Graph?.getLinkedObservationId(node);
      if (obsId) openObservationDrawer(obsId);
      else showToast(`Механизм: ${node.label}`, 'info');
      break;
    }
  }
}
